import React, { useEffect, useMemo, useState } from 'react';
import { Mail, MessageSquare, MessagesSquare, RefreshCw, AlertCircle } from 'lucide-react';
import { Customer, Communication } from '../types';
import { api } from '../services/api';
import { Avatar, Badge, Button, Card } from './ui';

interface CommunicationHistoryProps {
  customers: Customer[];
}

const fmtDateTime = (timestamp: string | null) => {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const cleanFrom = (raw: string) => {
  if (!raw) return '';
  // "Name <email@x.com>" -> "Name"; bare "email@x.com" -> the email.
  const namePart = raw.split('<')[0].trim().replace(/^"|"$/g, '');
  return namePart || raw.trim();
};

const CommunicationHistory: React.FC<CommunicationHistoryProps> = ({
  customers,
}) => {
  const [comms, setComms] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Filter chips
  const [filterCustomerId, setFilterCustomerId] = useState<string | null>(null);
  const [filterDirection, setFilterDirection] = useState<
    'all' | 'inbound' | 'outbound'
  >('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getCommunications()
      .then((data) => {
        if (cancelled) return;
        setComms(data || []);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to load communications';
        setError(message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const customerById = useMemo(
    () => new Map(customers.map((c) => [c.id, c])),
    [customers]
  );

  const filtered = useMemo(() => {
    return comms.filter((c) => {
      if (filterCustomerId && c.customerId !== filterCustomerId) return false;
      if (filterDirection !== 'all' && c.direction !== filterDirection)
        return false;
      return true;
    });
  }, [comms, filterCustomerId, filterDirection]);

  const customersWithComms = useMemo(() => {
    const ids = new Set(comms.map((c) => c.customerId));
    return customers
      .filter((c) => ids.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [comms, customers]);

  return (
    <div className="p-3 md:p-6 max-w-4xl">
      <header className="mb-4 md:mb-5 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold text-sand-900 font-display">
            Unified inbox
          </h3>
          <p className="text-sm text-sand-500">
            Emails to and from your guests, pulled live from Gmail (last 12 months).
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          onClick={() => setReloadKey((k) => k + 1)}
          disabled={loading}
        >
          Refresh
        </Button>
      </header>

      {/* Filters */}
      {comms.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <FilterChip
            active={filterDirection === 'all'}
            onClick={() => setFilterDirection('all')}
          >
            All
          </FilterChip>
          <FilterChip
            active={filterDirection === 'inbound'}
            onClick={() => setFilterDirection('inbound')}
          >
            Inbound
          </FilterChip>
          <FilterChip
            active={filterDirection === 'outbound'}
            onClick={() => setFilterDirection('outbound')}
          >
            Outbound
          </FilterChip>
          <span className="mx-1 h-4 w-px bg-sand-200" />
          <select
            value={filterCustomerId || ''}
            onChange={(e) => setFilterCustomerId(e.target.value || null)}
            className="text-xs h-8 rounded-full border border-sand-200 bg-white px-3 text-sand-700"
          >
            <option value="">All customers</option>
            {customersWithComms.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-sand-500">
            {filtered.length} of {comms.length}
          </span>
        </div>
      )}

      {loading && <LoadingSkeleton />}

      {!loading && error && (
        <Card className="p-5 border-red-200 flex items-start gap-3">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-sand-900">
              Couldn't load communications
            </p>
            <p className="text-sm text-sand-600 mt-1 leading-relaxed">{error}</p>
            <p className="text-xs text-sand-500 mt-2">
              If Gmail isn't configured yet, set the GMAIL_CLIENT_ID,
              GMAIL_CLIENT_SECRET, GMAIL_REDIRECT_URI and GMAIL_REFRESH_TOKEN
              environment variables on the App Service.
            </p>
          </div>
        </Card>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Card className="py-14 text-center border-dashed">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sand-100 text-sand-500 mb-3">
            <MessagesSquare className="h-5 w-5" />
          </div>
          <p className="font-medium text-sand-800">
            {comms.length === 0 ? 'No communications yet' : 'No matches'}
          </p>
          <p className="text-sand-500 text-sm mt-1">
            {comms.length === 0
              ? "When you exchange email with a guest, it'll appear here."
              : 'Try a different filter.'}
          </p>
        </Card>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((c, idx) => {
            const cust = customerById.get(c.customerId);
            const isInbound = c.direction === 'inbound';
            return (
              <div key={c.id} className="relative flex gap-4">
                {idx !== filtered.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="absolute top-9 left-[18px] -ml-px h-[calc(100%+0.75rem)] w-px bg-sand-200"
                  />
                )}

                <div className="relative shrink-0">
                  <span
                    className={[
                      'flex items-center justify-center h-9 w-9 rounded-full ring-4 ring-sand-50',
                      isInbound
                        ? 'bg-sky-50 text-sky-700'
                        : 'bg-sage-100 text-sage-700',
                    ].join(' ')}
                  >
                    <Mail className="h-4 w-4" />
                  </span>
                </div>

                <Card className="flex-1 min-w-0 p-4">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {cust && <Avatar name={cust.name} size="sm" />}
                      <p className="text-sm font-medium text-sand-900 truncate">
                        {c.customerName}
                      </p>
                      <Badge tone={isInbound ? 'sand' : 'blue'} uppercase>
                        {c.direction}
                      </Badge>
                    </div>
                    <span className="text-xs text-sand-500 shrink-0">
                      {fmtDateTime(c.timestamp)}
                    </span>
                  </div>

                  {c.subject && (
                    <p className="text-sm text-sand-900 font-medium truncate">
                      {c.subject}
                    </p>
                  )}
                  {c.snippet && (
                    <p className="text-sm text-sand-700 leading-relaxed mt-1 line-clamp-3">
                      {c.snippet}
                    </p>
                  )}

                  <div className="mt-3 pt-3 border-t border-sand-100 flex items-center justify-between text-[11px] text-sand-500 gap-2">
                    <span className="truncate">
                      {isInbound ? 'From' : 'To'}{' '}
                      {isInbound ? cleanFrom(c.from) : cleanFrom(c.to)}
                    </span>
                    <a
                      href={`https://mail.google.com/mail/u/0/#all/${c.threadId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-sage-700 hover:text-sage-900 shrink-0"
                    >
                      Open in Gmail
                    </a>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilterChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={[
      'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors',
      active
        ? 'bg-sage-50 border-sage-200 text-sage-800'
        : 'bg-white border-sand-200 text-sand-700 hover:bg-sand-50',
    ].join(' ')}
  >
    {children}
  </button>
);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex gap-4">
        <div className="h-9 w-9 rounded-full bg-sand-100 animate-pulse shrink-0" />
        <div className="flex-1 bg-white border border-sand-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-3 w-32 bg-sand-100 rounded animate-pulse" />
            <div className="h-3 w-14 bg-sand-100 rounded-full animate-pulse" />
          </div>
          <div className="h-3 w-3/4 bg-sand-100 rounded animate-pulse mb-2" />
          <div className="h-3 w-2/3 bg-sand-100 rounded animate-pulse" />
        </div>
      </div>
    ))}
  </div>
);

export default CommunicationHistory;
